import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { db } from "@/lib/supabase";
import { roleColorStyleFor, type Role } from "@/lib/role-formatting";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";

export type { Role } from "@/lib/role-formatting";

export interface UseRolesResult {
  roles: Role[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Look up a role by id. */
  getById: (id: string | null | undefined) => Role | undefined;
  /** Look up a role by its (unique) display name. */
  getByName: (name: string | null | undefined) => Role | undefined;
  /** Display name for a role id (empty string if unknown). */
  formatRole: (id: string | null | undefined) => string;
  /** Inline badge/swatch style ({color, backgroundColor}) for a role id. */
  roleColorStyle: (id: string | null | undefined) => CSSProperties;
  /** Sort order for a role id (Infinity if unknown, so unknowns sort last). */
  roleSortOrder: (id: string | null | undefined) => number;
}

const noop = async () => {};

export const RolesContext = createContext<UseRolesResult>({
  roles: [],
  loading: true,
  error: null,
  refresh: noop,
  getById: () => undefined,
  getByName: () => undefined,
  formatRole: () => "",
  roleColorStyle: () => ({}),
  roleSortOrder: () => Number.POSITIVE_INFINITY,
});

async function fetchRoles(): Promise<{ roles: Role[]; error: string | null }> {
  const { data, error } = await db.roles.getAll();
  if (error) return { roles: [], error: error.message ?? "Failed to load roles" };
  return { roles: data ?? [], error: null };
}

export function RolesProvider({ children }: { children: React.ReactNode }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `loading` reflects the initial load only (it starts true). Refreshes after a
  // save/edit/realtime change revalidate in the background and keep the current
  // roles on screen, so the settings table doesn't flicker through its loading
  // state on every mutation.
  const refresh = useCallback(async () => {
    const result = await fetchRoles();
    setRoles(result.roles);
    setError(result.error);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Refetch when roles change remotely (e.g. another tab edits the role list).
  // The list is tiny, so a non-silent refresh doesn't visibly flicker.
  useRealtimeRefresh(["roles"], refresh);

  const byId = useMemo(() => {
    const map = new Map<string, Role>();
    for (const role of roles) map.set(role.id, role);
    return map;
  }, [roles]);

  const byName = useMemo(() => {
    const map = new Map<string, Role>();
    for (const role of roles) map.set(role.display_name, role);
    return map;
  }, [roles]);

  const value = useMemo<UseRolesResult>(() => {
    const getById = (id: string | null | undefined) => (id ? byId.get(id) : undefined);
    return {
      roles,
      loading,
      error,
      refresh,
      getById,
      getByName: (name) => (name ? byName.get(name) : undefined),
      formatRole: (id) => getById(id)?.display_name ?? "",
      roleColorStyle: (id) => roleColorStyleFor(getById(id)),
      roleSortOrder: (id) => getById(id)?.sort_order ?? Number.POSITIVE_INFINITY,
    };
  }, [roles, loading, error, refresh, byId, byName]);

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function useRoles(): UseRolesResult {
  return useContext(RolesContext);
}
